// Blog service for managing blog posts in Firestore
import { firestoreService } from './firestore';

const COLLECTION_NAME = 'blogPosts';

export const blogService = {
  // Create a new blog post
  async createPost(postData) {
    try {
      const post = {
        title: postData.title,
        content: postData.content,
        excerpt: postData.excerpt,
        category: postData.category,
        author: postData.author,
        tags: postData.tags || [],
        image: postData.image || '📝',
        published: postData.published || false,
        readTime: postData.readTime || '5 min',
        createdAt: firestoreService.getServerTimestamp(),
        updatedAt: firestoreService.getServerTimestamp()
      };
      
      const postId = await firestoreService.create(COLLECTION_NAME, post);
      return postId;
    } catch (error) {
      console.error('Error creating blog post:', error);
      throw error;
    }
  },

  // Get all published blog posts
  async getAllPublishedPosts() {
    try {
      const posts = await firestoreService.query(
        COLLECTION_NAME,
        [{ field: 'published', operator: '==', value: true }],
        { field: 'createdAt', direction: 'desc' }
      );
      return posts;
    } catch (error) {
      console.error('Error getting published posts:', error);
      throw error;
    }
  },

  // Get all posts (including drafts)
  async getAllPosts() {
    try {
      const posts = await firestoreService.query(
        COLLECTION_NAME,
        [],
        { field: 'createdAt', direction: 'desc' }
      );
      return posts;
    } catch (error) {
      console.error('Error getting all posts:', error);
      throw error;
    }
  },

  // Get a single post by ID
  async getPostById(postId) {
    try {
      const post = await firestoreService.getById(COLLECTION_NAME, postId);
      return post;
    } catch (error) {
      console.error('Error getting post by ID:', error);
      throw error;
    }
  },

  // Update a blog post
  async updatePost(postId, postData) {
    try {
      const updateData = {
        ...postData,
        updatedAt: firestoreService.getServerTimestamp()
      };
      
      await firestoreService.update(COLLECTION_NAME, postId, updateData);
      return true;
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  },

  // Delete a blog post
  async deletePost(postId) {
    try {
      await firestoreService.delete(COLLECTION_NAME, postId);
      return true;
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  },

  // Get posts by category
  async getPostsByCategory(category) {
    try {
      const posts = await firestoreService.query(
        COLLECTION_NAME,
        [
          { field: 'category', operator: '==', value: category },
          { field: 'published', operator: '==', value: true }
        ],
        { field: 'createdAt', direction: 'desc' }
      );
      return posts;
    } catch (error) {
      console.error('Error getting posts by category:', error);
      throw error;
    }
  },

  // Get posts by tag
  async getPostsByTag(tag) {
    try {
      const posts = await firestoreService.query(
        COLLECTION_NAME,
        [
          { field: 'tags', operator: 'array-contains', value: tag },
          { field: 'published', operator: '==', value: true }
        ],
        { field: 'createdAt', direction: 'desc' }
      );
      return posts;
    } catch (error) {
      console.error('Error getting posts by tag:', error);
      throw error;
    }
  },

  // Toggle post published status
  async togglePublished(postId) {
    try {
      const post = await this.getPostById(postId);
      if (post) {
        await this.updatePost(postId, { published: !post.published });
        return !post.published;
      }
      return false;
    } catch (error) {
      console.error('Error toggling published status:', error);
      throw error;
    }
  },

  // Subscribe to real-time updates
  subscribeToPublishedPosts(callback) {
    try {
      return firestoreService.subscribeToCollection(
        COLLECTION_NAME,
        callback,
        [{ field: 'published', operator: '==', value: true }],
        { field: 'createdAt', direction: 'desc' }
      );
    } catch (error) {
      console.error('Error subscribing to posts:', error);
      throw error;
    }
  },

  // Get available categories
  async getCategories() {
    try {
      const posts = await this.getAllPublishedPosts();
      const categories = [...new Set(posts.map(post => post.category))];
      return categories;
    } catch (error) {
      console.error('Error getting categories:', error);
      throw error;
    }
  },

  // Get available tags
  async getTags() {
    try {
      const posts = await this.getAllPublishedPosts();
      const tags = [...new Set(posts.flatMap(post => post.tags))];
      return tags;
    } catch (error) {
      console.error('Error getting tags:', error);
      throw error;
    }
  }
};

export default blogService;